import { DeviceService, NotificationsService } from '@/api/client'
import { Capacitor } from '@capacitor/core'
import { useEventStore } from '@/stores/EventStore'
import { useAuthStore } from '@/stores/AuthStore'
import StoreManager from '@/classes/manager/StoreManager'
import type { GetTokenOptions } from '@capacitor-firebase/messaging'
import {
  FirebaseMessaging,
  type NotificationActionPerformedEvent,
} from '@capacitor-firebase/messaging'
import { navigateToCurrentTaskPage } from '@/router/actions/navigateToCurrentTaskPage'
import { DeviceType } from '@/api/client'
import { z } from 'zod'
import { Project } from '@/classes/entities/Project'
import { useConfigStore } from '@/stores/ConfigStore'
import { useEmployeeStore } from '@/stores/EmployeeStore'
import { useProjectStore } from '@/stores/ProjectStore'
import { initializeApp, getApps } from 'firebase/app'
import router from '@/router'
import { useAppStateStore } from '@/stores/AppStateStore'

/**
 * Notification types that trigger different sync behaviors
 */
const NOTIFICATION_TYPES = {
  OFFICE_SYNC: 'officeSync',
  PROJECT_SYNC: 'projectSync',
  DEVICE_CONFIG_SYNC: 'deviceConfigSync',
  SHIFT_PLAN: 'shiftPlan',
} as const

/**
 * Schema for validating incoming push notifications
 * Silent notifications without data are expected and will fail validation
 */
const pushNotificationSchema = z.object({
  notification: z.object({
    data: z.object({
      type: z.string().optional(),
      id: z.union([z.string(), z.number()]).optional(),
      notification_id: z.string().optional(),
    }),
  }),
})

const webFirebaseConfigDev = {
  apiKey: 'AIzaSyARZRxQh_wqy78o7DA9ZGNr2p-Dhrl2njQ',
  authDomain: 'clockin-app-development.firebaseapp.com',
  projectId: 'clockin-app-development',
  storageBucket: 'clockin-app-development.appspot.com',
  messagingSenderId: '861196756640',
  appId: '1:861196756640:web:1a474734255523e1a84bc5',
  measurementId: 'G-3R8GGNSBN5',
  vapidKey:
    'BHIId4Wf72A_dTD_nQljbxbosB6aOSNfaYZghNOlPUz2mJxw-usGNI3F15jJpX1nYO0p3NT9ZwQr5pMqHkqsAtA',
}

const webFirebaseConfigProd = {
  apiKey: 'AIzaSyARq9b9CuhGuWzBOud1y2O6rAYCJl7QYi4',
  authDomain: 'clockin-app-prod.firebaseapp.com',
  projectId: 'clockin-app-prod',
  storageBucket: 'clockin-app-prod.firebasestorage.app',
  messagingSenderId: '801401348357',
  appId: '1:801401348357:web:f018c1e5956c087987176c',
  measurementId: 'G-K22GE5MC1L',
  vapidKey:
    'BG62vrpwEe6fXeHelZpy2lngJtvuIFTpz4JNl5vXlLz7XJqYtFtJxrYpAZNsNbFsyzBqtSUseQsPH6B5T6N_rrY',
}

/**
 * PushNotificationManager
 *
 * Manages push notifications for the ClockIn app across web and native platforms.
 * Handles Firebase Cloud Messaging setup, token registration, and notification processing.
 *
 * Public API:
 * - start(): Initialize and start handling push notifications
 * - stop(): Stop handling notifications and cleanup
 * - isStarted(): Check if notifications are currently active
 *
 * Usage:
 * - Call start() on app initialization (AppConfiguration) and after login
 * - Call stop() on logout to clean up resources
 */
export default class PushNotificationManager {
  // Service worker registration for web platform (cached)
  private static serviceWorkerRegistration:
    | ServiceWorkerRegistration
    | undefined = undefined

  // Track if push notifications are currently active
  private static started = false

  /**
   * Check if push notifications are currently started
   */
  static isStarted(): boolean {
    return PushNotificationManager.started
  }

  /**
   * Start push notification handling
   *
   * This method:
   * 1. Checks if already started (prevents duplicate initialization)
   * 2. Requests permissions from the user if needed
   * 3. Registers the device token with the server
   * 4. Sets up listeners for incoming notifications
   *
   * Safe to call multiple times - will only initialize once
   */
  static async start(): Promise<void> {
    console.debug('[PushNotificationManager] start() called')

    // Skip in development mode
    if (import.meta.env.DEV) {
      console.debug('[PushNotificationManager] Skipping in development mode')
      return
    }

    // Prevent duplicate initialization
    if (PushNotificationManager.started) {
      console.debug('[PushNotificationManager] Already started, skipping')
      return
    }

    try {
      // Step 1: Request and check permissions
      const hasPermission = await PushNotificationManager.requestPermission()
      if (!hasPermission) {
        console.debug('[PushNotificationManager] Permission not granted')
        return
      }

      // Step 2: Setup platform-specific configuration (Firebase for web)
      await PushNotificationManager.setupPlatform()

      // Step 3: Get Firebase token and register with server
      try {
        await PushNotificationManager.registerToken()
      } catch (e) {
        if (
          e instanceof Error &&
          e.message !== 'Failed to register token at backend'
        ) {
          throw e
        }
        // Token registration at backend failed, but we continue to setup listeners
      }

      // Step 4: Start listening for incoming notifications
      await PushNotificationManager.setupNotificationListeners()

      PushNotificationManager.started = true
      console.debug('[PushNotificationManager] Successfully started')
    } catch (error) {
      console.error('[PushNotificationManager] Failed to start:', error)
      // Reset state on failure (but don't throw - app should continue)
      PushNotificationManager.started = false
    }
  }

  /**
   * Stop push notification handling and cleanup
   *
   * This method:
   * 1. Removes all notification listeners
   * 2. Clears delivered notifications (native only)
   * 3. Deletes the Firebase token
   * 4. Resets internal state
   *
   * Call this on logout to properly cleanup resources
   */
  static async stop(): Promise<void> {
    console.debug('[PushNotificationManager] stop() called')

    if (!PushNotificationManager.started) {
      console.debug('[PushNotificationManager] Not started, nothing to stop')
      return
    }

    try {
      // Remove all Firebase listeners
      await FirebaseMessaging.removeAllListeners()

      // Clear delivered notifications (native platforms only)
      if (Capacitor.getPlatform() !== 'web') {
        await FirebaseMessaging.removeAllDeliveredNotifications()
      }

      // Delete the Firebase token
      await FirebaseMessaging.deleteToken()

      PushNotificationManager.started = false
      console.debug('[PushNotificationManager] Successfully stopped')
    } catch (error) {
      console.error('[PushNotificationManager] Error during stop:', error)
      // Force reset state even if cleanup fails
      PushNotificationManager.started = false
    }
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Request push notification permissions from the user
   * Returns true if granted, false otherwise
   */
  private static async requestPermission(): Promise<boolean> {
    console.debug('[PushNotificationManager] Requesting permissions')

    // Mark that permission dialog might background the app
    useAppStateStore().appBackgroundedByPermissionCheck = true

    let permissionStatus: string

    if (Capacitor.getPlatform() === 'web') {
      // Web: Use Notification API
      if (typeof Notification === 'undefined') {
        console.warn('[PushNotificationManager] Notification API not available')
        return false
      }
      permissionStatus = await Notification.requestPermission()
    } else {
      // Native: Use Firebase Messaging
      const status = await FirebaseMessaging.checkPermissions()
      permissionStatus = status.receive

      // Request if not yet decided
      if (permissionStatus === 'prompt') {
        const result = await FirebaseMessaging.requestPermissions()
        permissionStatus = result.receive
      }
    }

    const granted = permissionStatus === 'granted'
    console.debug(
      `[PushNotificationManager] Permission ${granted ? 'granted' : 'denied'}`,
    )
    return granted
  }

  /**
   * Setup platform-specific configuration
   * For web: Initialize Firebase and register service worker
   * For native: No setup needed (handled by native Firebase SDK)
   */
  private static async setupPlatform(): Promise<void> {
    if (Capacitor.getPlatform() !== 'web') {
      // Native platforms don't need additional setup
      return
    }

    console.debug('[PushNotificationManager] Setting up for web platform')

    // Get Firebase config
    const config = PushNotificationManager.getWebConfig()
    if (!config) {
      throw new Error('Firebase config not available for web platform')
    }

    // Initialize Firebase app (only once)
    if (getApps().length === 0) {
      console.debug('[PushNotificationManager] Initializing Firebase app')
      initializeApp(config)
    } else {
      console.debug(
        '[PushNotificationManager] Firebase app already initialized',
      )
    }

    // Register service worker for message handling
    await PushNotificationManager.getFirebaseServiceWorkerRegistration()
  }

  /**
   * Get and register the Firebase Cloud Messaging token with the server
   */
  private static async registerToken(): Promise<void> {
    console.debug('[PushNotificationManager] Registering token')

    // Get token options (platform-specific)
    const tokenOptions = await PushNotificationManager.buildGetTokenOptions()

    // Get Firebase token
    const { token } = await FirebaseMessaging.getToken(tokenOptions)
    console.debug('[PushNotificationManager] Token received')

    // Send token to backend
    try {
      await DeviceService.storePushToken({
        push_token: token,
        device_type: Capacitor.getPlatform(),
      })
      console.debug('[PushNotificationManager] Token registered with backend')
    } catch (error) {
      console.error(
        '[PushNotificationManager] Failed to register token at backend:',
        error,
      )
      throw new Error('Failed to register token at backend')
    }
  }

  /**
   * Setup listeners for incoming notifications
   * Handles both foreground notifications and notification tap actions
   */
  private static async setupNotificationListeners(): Promise<void> {
    console.debug('[PushNotificationManager] Setting up notification listeners')

    // Listen for notifications received while app is in foreground
    await FirebaseMessaging.addListener(
      'notificationReceived',
      (notification) =>
        void PushNotificationManager.handleNotification(notification),
    )

    // Listen for user tapping on a notification
    await FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (event: NotificationActionPerformedEvent) => {
        console.debug('[PushNotificationManager] Notification tapped')

        // Process the notification data
        void PushNotificationManager.handleNotification(event)

        // Handle shift plan notifications specially (mark as read and navigate)
        void PushNotificationManager.handleShiftPlanAction(event)
      },
    )
  }

  /**
   * Handle an incoming notification
   * Processes the notification payload and triggers appropriate syncs
   */
  private static async handleNotification(
    notification: unknown,
  ): Promise<void> {
    console.debug('[PushNotificationManager] Processing notification')

    // Validate notification structure
    const result = pushNotificationSchema.safeParse(notification)
    if (!result.success) {
      // This is expected for "loud" notifications without custom data
      console.debug(
        '[PushNotificationManager] Notification has no custom data (expected for loud notifications)',
      )
      return
    }

    const { type, id } = result.data.notification.data
    console.debug(
      `[PushNotificationManager] Processing notification type: ${type}`,
    )

    // Handle different notification types
    switch (type) {
      case NOTIFICATION_TYPES.OFFICE_SYNC:
        // Sync events and navigate to current task (except on terminals)
        await StoreManager.sync([useEventStore()])
        if (useAuthStore().device_type !== DeviceType.TERMINAL) {
          void navigateToCurrentTaskPage(true)
        }
        break

      case NOTIFICATION_TYPES.PROJECT_SYNC:
        // Sync a specific project
        if (id && typeof id === 'number') {
          await Project.syncOne(id)
          // Refresh if this is the active project
          if (useProjectStore().project?.id === id) {
            await useProjectStore().setActiveProjectById(id)
          }
        }
        break

      case NOTIFICATION_TYPES.DEVICE_CONFIG_SYNC:
        // Sync device configuration and employees
        void useConfigStore().sync()
        void useEmployeeStore().sync()
        break

      case NOTIFICATION_TYPES.SHIFT_PLAN:
        // Shift plan notifications are handled in handleShiftPlanAction
        break

      default:
        console.debug(
          `[PushNotificationManager] Unknown notification type: ${type}`,
        )
    }
  }

  /**
   * Handle shift plan notification tap action
   * Marks the notification as read and navigates to shift plan page
   */
  private static async handleShiftPlanAction(event: unknown): Promise<void> {
    const result = pushNotificationSchema.safeParse(event)
    if (!result.success) {
      return
    }

    const { type, notification_id } = result.data.notification.data

    if (type === NOTIFICATION_TYPES.SHIFT_PLAN && notification_id) {
      console.debug(
        `[PushNotificationManager] Handling shift plan notification ${notification_id}`,
      )

      // Navigate to shift plan page
      void router.push({ name: 'shiftPlan' })

      // Mark notification as read on server
      try {
        await NotificationsService.markNotificationAsRead(
          String(notification_id),
        )
        console.debug(
          `[PushNotificationManager] Marked notification ${notification_id} as read`,
        )
      } catch (error) {
        console.error(
          '[PushNotificationManager] Failed to mark notification as read:',
          error,
        )
      }
    }
  }

  /**
   * Get or register the Firebase service worker for web platform
   * Service worker handles background notifications on web
   */
  private static async getFirebaseServiceWorkerRegistration(): Promise<
    ServiceWorkerRegistration | undefined
  > {
    if (Capacitor.getPlatform() !== 'web') {
      return undefined
    }

    // Return cached registration if available
    if (PushNotificationManager.serviceWorkerRegistration) {
      return PushNotificationManager.serviceWorkerRegistration
    }

    // Determine which service worker to use based on environment
    const swPath = window.location.hostname.includes('app.clockin.de')
      ? '/firebase-messaging-sw-prod.js'
      : '/firebase-messaging-sw-dev.js'

    // Register service worker
    const registration = await navigator.serviceWorker.register(swPath)
    PushNotificationManager.serviceWorkerRegistration = registration

    return registration
  }

  /**
   * Build token options for Firebase getToken call
   * Different options needed for web vs native platforms
   */
  private static async buildGetTokenOptions(): Promise<
    GetTokenOptions | undefined
  > {
    if (Capacitor.getPlatform() === 'web') {
      const config = PushNotificationManager.getWebConfig()
      if (!config) {
        return undefined
      }

      return {
        serviceWorkerRegistration:
          await PushNotificationManager.getFirebaseServiceWorkerRegistration(),
        vapidKey: config.vapidKey,
      }
    }

    // Native platforms don't need special options
    return {}
  }

  /**
   * Get Firebase configuration for web platform
   * Returns different config based on environment (dev vs prod)
   */
  private static getWebConfig() {
    // No web push in development mode
    if (import.meta.env.MODE === 'development') {
      return undefined
    }

    // Determine environment based on hostname
    return window.location.hostname.includes('app.clockin.de')
      ? webFirebaseConfigProd
      : webFirebaseConfigDev
  }
}
